import { Fetch } from "@lucaswickstrom/fetch";
import { useMutationWithAuthError } from "@ssf/auth-components";
import type { MutationOptions, QueryOptions } from "@ssf/components";
import {
  type Club,
  type District,
  type Member,
  type Schema,
  SpecialType,
  type UnSchema,
} from "@ssf/memdb-api";
import { getIssues } from "@ssf/memdb-components";
import { PostgrestClient } from "@supabase/postgrest-js";

import { useQueryWrapper } from "./index.js";

export interface AutoAskOfNewMembership {
  id: string /* primary key */;
  createdAt: string;
  createdBy: number;
  clubId: number;
}

export interface AutoAsked {
  id: string /* primary key */;
  createdAt: string;
  memberId: number;
  type: "club" | "membership";
  by: "user" | "auto" | "done";
}

export type Document = {
  id: string /* primary key */;
  createdAt: string;
  createdBy: number;
} & {
  type: "membership";
  meta: {
    member: Pick<
      Member,
      "id" | "name" | "birthday" | "sex" | "street" | "zipcode" | "city"
    >;
    club: Pick<Club, "id" | "name">;
    district: Pick<District, "id">;
  };
};

export interface Signature {
  id: string /* primary key */;
  documentId: string /* foreign key to documents.id */;
  createdAt: string;
  signedAt?: string;
  signatory: number;
  meta: {
    ip?: string;
    agent?: string;
  } & {
    access_token: string;
  };
  documents?: Document;
}

const SIGN_API_URL = process.env.SIGN_API_URL;
if (!SIGN_API_URL) {
  throw new Error("env `SIGN_API_URL` is not set");
}

export const signApi = new Fetch({ baseUrl: SIGN_API_URL });
export const supabase = new PostgrestClient(`${SIGN_API_URL}/rest/v1`);

export function setAuthorization(access_token?: string) {
  if (access_token) {
    const Authorization = `Bearer ${access_token}`;
    signApi.options = { ...signApi.options, headers: { Authorization } };
    supabase.headers.Authorization = Authorization;
  }
}

type Prettify<T> = { [K in keyof T]: T[K] } & {};
const clubSchema = {
  id: 1,
  name: 1,
  startdate: 1,
  noeconomy: 1,
  specialType: 1,
  schoolclub: 1,
  active: 1,
  approvalStatus: [{ referenceid: 1, approved: 1, signed: 1, documentid: 1 }],
} satisfies Schema<Club>;

function autoAsk() {
  return signApi
    .get<{
      clubs: Prettify<
        UnSchema<Club, typeof clubSchema> & { existing?: boolean }
      >[];
      memberships: { id: number; name: string; existing?: boolean }[];
    }>("/autoAsk")
    .then(({ clubs, memberships }) => ({
      clubs: clubs.map((club) => ({
        ...club,
        issues: getIssues({
          clubStatus: club.approvalStatus,
          isClubApproved: club.specialType !== SpecialType.Temp,
        }),
      })),
      memberships,
    }));
}

export const useAutoAsk = (options?: QueryOptions<typeof autoAsk>) => {
  return useQueryWrapper(["useAutoAsk"], autoAsk, options);
};

async function autoAskForMember(memberId: number) {
  return signApi.get<{
    clubs: Prettify<
      UnSchema<Club, typeof clubSchema> & { existing?: boolean }
    >[];
    memberships: { id: number; name: string; existing?: boolean }[];
  }>("/autoAskForMember", { params: { memberId } });
}

export const useAutoAskForMember = (
  memberId: number,
  options?: QueryOptions<typeof autoAskForMember>,
) => {
  return useQueryWrapper(
    ["useAutoAskForMember", memberId],
    () => autoAskForMember(memberId),
    { ...options, enabled: !!memberId && options?.enabled !== false },
  );
};

async function createAutoAsked(autoAsked: Partial<AutoAsked>) {
  return supabase.from("autoAsked").insert(autoAsked);
}
export const useCreateAutoAsked = (
  options?: MutationOptions<typeof createAutoAsked>,
) =>
  useMutationWithAuthError(
    ["useCreateAutoAsk"],
    async (autoAsked: Partial<AutoAsked>) => {
      const data = await supabase
        .from("autoAsked")
        .insert(autoAsked)
        .throwOnError();
      return data;
    },
    options,
  );

export async function removeAuthAsked(memberId: number) {
  return supabase
    .from("autoAsked")
    .delete()
    .match({ memberId, by: "auto" })
    .gt("createdAt", new Date(Date.now() - 60 * 60 * 1000).toISOString())
    .throwOnError();
}

async function deleteAutoAskedStatus(memberId: number) {
  return signApi.delete("/autoAskStatus", {
    params: { memberId, type: "club" },
  });
}

export const useDeleteAutoAskedStatus = (
  options?: MutationOptions<typeof deleteAutoAskedStatus>,
) =>
  useMutationWithAuthError(
    ["useDeleteAutoAskedStatus"],
    deleteAutoAskedStatus,
    options,
  );

async function toggleAutoAskOfNewMembership({
  clubId,
  memberId,
  enabled,
}: {
  clubId: number;
  memberId: number;
  enabled: boolean;
}) {
  if (enabled) {
    return signApi.post<AutoAskOfNewMembership>("/autoAskOfNewMembership", {
      params: { clubId, memberId },
    });
  }
  return signApi.delete("/autoAskOfNewMembership", {
    params: { clubId, memberId },
  });
}

export const useToggleAutoAskOfNewMembership = (
  options?: MutationOptions<typeof toggleAutoAskOfNewMembership>,
) =>
  useMutationWithAuthError(
    ["useToggleAutoAskOfNewMembership"],
    toggleAutoAskOfNewMembership,
    options,
  );

async function createSignClub(signClub: Partial<AutoAskOfNewMembership>) {
  const data = await supabase
    .from("autoAskOfNewMembership")
    .insert(signClub)
    .throwOnError();

  supabase
    .from("autoAsked")
    .insert({
      memberId: signClub.createdBy,
      type: "club",
      by: "done",
    })
    .throwOnError();

  return data;
}
export const useCreateSignClub = (
  options?: MutationOptions<typeof createSignClub>,
) => useMutationWithAuthError(["useCreateSignClub"], createSignClub, options);

async function signMembership(body: { clubId: number }) {
  return signApi.post("/sign", { body });
}

export const useSignMembership = (
  options?: MutationOptions<typeof signMembership>,
) => useMutationWithAuthError(["useSignMembership"], signMembership, options);

async function getAutoAskStatus(memberId: number, clubId: number) {
  return signApi.get<{
    autoAsked?: AutoAsked;
    autoAskOfNewMembership?: AutoAskOfNewMembership;
  }>("/autoAskStatus", { params: { memberId, clubId } });
}

export const useAutoAskStatus = (
  memberId: number,
  clubId: number,
  options?: QueryOptions<typeof getAutoAskStatus>,
) =>
  useQueryWrapper(
    ["useAutoAskStatus", memberId, clubId],
    () => getAutoAskStatus(memberId, clubId),
    {
      ...options,
      enabled: !!memberId && !!clubId && options?.enabled !== false,
    },
  );
